import React, { useState } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import {
  AppBar,
  Box,
  Button,
  Drawer,
  IconButton,
  Stack,
  Toolbar,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import MenuRoundedIcon from '@mui/icons-material/MenuRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import NavigationMenu, { AccountMenu } from '../components/NavigationMenu';
import RightRail from '../components/widgets/RightRail';
import { legalFooterLinks } from '../constants/legal';
import CookieConsent from '../components/CookieConsent';
import { ANALYTICS_SETTINGS_EVENT } from '../utils/analytics';

const sidebarWidth = 274;
const rightRailWidth = 344;

const AppLayout = ({ children }) => {
  const theme = useTheme();
  const location = useLocation();
  // This app renders in the browser: use the actual viewport on the first render.
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'), { noSsr: true });
  const [mobileOpen, setMobileOpen] = useState(false);
  const showRightRail = isDesktop && location.pathname === '/';
  const contentPaddingX = showRightRail
    ? { xs: '2px', sm: '2px', md: 3 }
    : '2px';

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', color: 'text.primary' }}>
      {isDesktop ? (
        <Box
          component="aside"
          sx={{
            position: 'fixed',
            inset: '0 auto 0 0',
            width: sidebarWidth,
            borderRight: '1px solid rgba(255,255,255,0.08)',
            bgcolor: 'background.sidebar',
            backgroundImage: 'linear-gradient(180deg, rgba(13, 214, 142, 0.08), rgba(255,255,255,0) 34%)',
            zIndex: theme.zIndex.drawer,
          }}
        >
          <NavigationMenu />
        </Box>
      ) : (
        <>
          <AppBar
            position="sticky"
            elevation={0}
            sx={{
              bgcolor: 'rgba(12, 17, 21, 0.94)',
              backdropFilter: 'blur(14px)',
              borderBottom: '1px solid rgba(255,255,255,0.08)',
            }}
          >
            <Toolbar sx={{ gap: 1.25, minHeight: 62 }}>
              <IconButton edge="start" onClick={() => setMobileOpen(true)} aria-label="Otwórz menu">
                <MenuRoundedIcon />
              </IconButton>
              <Box component={RouterLink} to="/" sx={{ color: 'inherit', textDecoration: 'none', minWidth: 0, flex: 1 }}>
                <Typography sx={{ fontWeight: 900, letterSpacing: 0, lineHeight: 1.1 }}>
                  SYMULIGA.PL
                </Typography>
                <Typography sx={{ color: 'text.secondary', fontSize: '0.72rem', lineHeight: 1.1 }}>
                  Polska piłka w liczbach
                </Typography>
              </Box>
              <Button
                component={RouterLink}
                to="/"
                startIcon={<SearchRoundedIcon />}
                sx={{ display: { xs: 'none', sm: 'inline-flex' }, color: 'text.primary', textTransform: 'none' }}
              >
                Szukaj
              </Button>
              <AccountMenu compact />
            </Toolbar>
          </AppBar>

          <Drawer
            open={mobileOpen}
            onClose={() => setMobileOpen(false)}
            ModalProps={{ keepMounted: true }}
            PaperProps={{
              sx: {
                width: { xs: '86vw', sm: 340 },
                maxWidth: 380,
                bgcolor: 'background.sidebar',
                backgroundImage: 'linear-gradient(180deg, rgba(13, 214, 142, 0.08), rgba(255,255,255,0) 42%)',
              },
            }}
          >
            <NavigationMenu mobile onNavigate={() => setMobileOpen(false)} />
          </Drawer>
        </>
      )}

      <Box
        sx={{
          pl: { lg: `${sidebarWidth}px` },
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: showRightRail ? `minmax(0, 1fr) ${rightRailWidth}px` : 'minmax(0, 1fr)',
            gap: { xs: 0, lg: 3 },
            maxWidth: showRightRail ? 1660 : 1320,
            width: '100%',
            mx: 'auto',
            px: contentPaddingX,
            py: { xs: 2, md: 3 },
            flex: 1,
          }}
        >
          <Box component="main" sx={{ minWidth: 0, minHeight: { xs: 'calc(100svh - 96px)', lg: 'calc(100svh - 48px)' } }}>
            {children}
          </Box>

          {showRightRail && (
            <Box component="aside" sx={{ minWidth: 0 }}>
              <RightRail />
            </Box>
          )}
        </Box>

        <Stack
          component="footer"
          justifyContent="center"
          alignItems="center"
          spacing={1}
          sx={{
            width: '100%',
            px: contentPaddingX,
            py: 2.5,
            borderTop: '1px solid rgba(255,255,255,0.08)',
            bgcolor: 'rgba(255,255,255,0.025)',
            color: 'text.secondary',
            fontSize: '0.78rem',
            textAlign: 'center',
          }}
        >
          <Typography variant="body2">© 2026 SymuLiga.pl</Typography>
          <Typography variant="body2">Symulacje, statystyki i narzędzia dla polskiej piłki.</Typography>
          <Stack direction="row" spacing={1.5} useFlexGap flexWrap="wrap" justifyContent="center">
            <Button size="small" color="inherit" sx={{ textTransform: 'none', fontSize: '0.75rem' }}
              onClick={() => window.dispatchEvent(new Event(ANALYTICS_SETTINGS_EVENT))}>
              Ustawienia analityki
            </Button>
            {legalFooterLinks.map((link) => (
              <Typography
                key={link.slug}
                component={RouterLink}
                to={link.path}
                variant="caption"
                sx={{
                  color: 'text.secondary',
                  textDecoration: 'none',
                  '&:hover': { color: 'primary.main', textDecoration: 'underline' },
                }}
              >
                {link.label}
              </Typography>
            ))}
          </Stack>
        </Stack>
      </Box>
      <CookieConsent />
    </Box>
  );
};

export default AppLayout;
